import React from 'react';
import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import { FusionTrendCard } from '@/components/pages/AssetsStudioPage/MainContent/Diagnostics/FusionTrend/FusionTrendCard';
import MaintenanceCard from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Maintenance/MaintenanceCard';
import { ManagedGraphsSection } from '@/modules/shared/DataLayers/components/ManagedGraphsSection';
import type { DataLayerFilter } from '../components/DataLayerFilters';
import { NewSpectrum } from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Spectrum/NewSpectrum';
import { SENSOR_FUSION_DISPLAY_MODES } from './sensor-fusion-display-modes';
import type { Diagnostics } from '@/types/api/diagnostics.types';
import type { IntervalLine, VerticalLine as DataLayerVerticalLine } from '@/modules/shared/DataLayers/types';
import type { useVerticalLines } from '@/hooks/shared/DataLayers/use-vertical-lines';

interface GraphData {
  id: string;
  title?: string;
  unit?: string;
  legend?: Array<{
    id: string;
    name: string;
    displayName?: string;
    enabled?: boolean;
    severity?: string;
    priority?: string;
    interface?: string;
    metadata?: Record<string, unknown>;
  }>;
  sensorUiNames?: string[];
}

interface BackendFilterOption {
  id: string;
  value: string;
  label: string;
  checked: boolean;
}

interface BackendFilter {
  id: string;
  title?: string;
  label?: string;
  type: 'search' | 'group' | 'dropdown' | 'chips' | 'expandable_legend' | string;
  isMultiSelected?: boolean;
  isMultiSelect?: boolean;
  options?: BackendFilterOption[];
  handler?: (value: unknown) => void;
  searchConfig?: Record<string, unknown>;
}

// Use the DataLayerVerticalLine type instead of defining our own
type VerticalLine = DataLayerVerticalLine;

interface Manager {
  selectedSections: string[];
  handleSelectionChange: (sections: string[]) => void;
  loadingState: boolean;
  triggerReset: boolean;
  handleReset: () => void;
  handleDataLayerReset: () => void;
  handleLoadingStateChange: (isLoading: boolean) => void;
}

interface SensorFusionLoadingStates {
  [key: string]: boolean;
}

interface AssetData {
  [key: string]: unknown;
}

interface ComponentData {
  component_name: string;
  component_ui_name?: string;
  [key: string]: unknown;
}

interface DiagnosticData {
  [key: string]: unknown;
}

interface SearchOption {
  value: string;
  label: string;
  unit: string;
  enabled: boolean;
  color?: string;
  section: string;
  sectionId: string;
}

export interface DataLayerConfig {
  id: string;
  condition?: () => boolean;
  component: () => React.ReactNode;
}

/**
 * Display Mode Configuration - defines which layers, graphs, and UI elements are visible in each mode
 *
 * DESIGN PHILOSOPHY: Support both whitelist (visible) and blacklist (hidden) approaches
 * - Use visibleLayers/visibleGraphsInLayers when you want to explicitly list what TO show
 * - Use hiddenLayers/hiddenGraphsInLayers when you want to explicitly list what NOT to show
 * - Visible takes precedence over hidden if both are specified
 *
 * @property visibleLayers - Array of layer IDs to show (empty = use activeDataLayers from metadata)
 * @property hiddenLayers - Array of layer IDs to hide but keep mounted
 * @property visibleGraphsInLayers - Per-layer filter functions that return true to SHOW a graph (whitelist)
 * @property hiddenGraphsInLayers - Per-layer filter functions that return true to HIDE a graph (blacklist)
 * @property visibleUIElements - Per-layer array of UI element IDs to show (whitelist)
 * @property hiddenUIElements - Per-layer array of UI element IDs to hide (blacklist)
 * @property autoSelectFilteredGraphs - Per-layer boolean to auto-select all filtered graphs in Manage Graphs
 */
export interface DisplayModeConfig {
  visibleLayers: string[];
  hiddenLayers?: string[];
  visibleGraphsInLayers?: Record<string, (graph: GraphData) => boolean>;
  hiddenGraphsInLayers?: Record<string, (graph: GraphData) => boolean>;
  visibleUIElements?: Record<string, string[]>;
  hiddenUIElements?: Record<string, string[]>;
  autoSelectFilteredGraphs?: Record<string, boolean>;
}

// Helper function to transform filters from backend to frontend format
export const transformFilters = (backendFilters: BackendFilter[]): DataLayerFilter[] => {
  if (!backendFilters || backendFilters.length === 0) return [];

  return backendFilters
    .filter((filter) => filter != null)
    .map((filter: BackendFilter) => {
      // Group filters have a nested structure (GroupOuterFilter -> GroupInnerFilter -> FilterOption)
      // that DataLayerFilterGroup component expects unchanged
      if (filter.type === 'group') {
        return filter as unknown as DataLayerFilter;
      }

      // For 'chips' type filters with the right structure, pass through but normalize isMultiSelect
      if (filter.type === 'chips' && filter.options?.[0]?.value) {
        return {
          id: filter.id,
          type: 'chips' as const,
          label: filter.title || filter.label || filter.id,
          isMultiSelect: filter.isMultiSelected !== undefined ? filter.isMultiSelected : filter.isMultiSelect || false,
          options: filter.options?.map((opt: BackendFilterOption) => ({
            value: opt.value,
            label: opt.label,
            enabled: opt.checked,
          })) || [],
          handler: filter.handler,
        };
      }

      // For 'search' type filters, pass through with configuration
      if (filter.type === 'search') {
        // Preserve search configuration from backend
        const searchFilter: DataLayerFilter = {
          id: filter.id,
          type: 'search' as const,
          label: filter.title || filter.label || filter.id,
          isMultiSelect: filter.isMultiSelected !== undefined ? filter.isMultiSelected : filter.isMultiSelect || false,
          searchConfig: filter.searchConfig || {},
          options: [], // Options will be populated dynamically
          handler: filter.handler,
        };
        return searchFilter;
      }

      // Use the filter id and title directly from backend
      const filterId = filter.id;
      const filterLabel = filter.title || filter.label || filter.id;

      const transformedFilter: DataLayerFilter = {
        id: filterId,
        label: filterLabel,
        type: (filter.type as 'search' | 'group' | 'dropdown' | 'chips' | 'expandable_legend') || 'dropdown',
        isMultiSelect: filter.isMultiSelected !== undefined ? filter.isMultiSelected : filter.isMultiSelect || false,
        options:
          filter.options?.map((opt: BackendFilterOption) => ({
            value: opt.value,
            label: opt.label,
            enabled: opt.checked,
          })) || [],
        defaultValue: filter.isMultiSelected
          ? filter.options?.filter((opt: BackendFilterOption) => opt.checked).map((opt: BackendFilterOption) => opt.value) || []
          : filter.options?.find((opt: BackendFilterOption) => opt.checked)?.value || ''
      };

      // Attach a generic handler if one is needed
      if (!filter.handler) {
        transformedFilter.handler = (value) => {
          // Specific filter logic will be handled by the parent component
          // through the onFilterChange callback
        };
      }

      return transformedFilter;
    });
};

/**
 * Declarative configuration for all data layers
 */
interface DataLayersConfigProps {
  activeDataLayers: string[];
  isMobileAsset: boolean;
  assetName?: string;
  diagnostics: Diagnostics[];
  baseTitle?: string;
  from: string;
  to: string;
  sensorGraphs: GraphData[];
  oilGraphs: GraphData[];
  coolantGraphs: GraphData[];
  alarmGraphs: GraphData[];
  sensorsManager: Manager;
  oilManager: Manager;
  coolantManager: Manager;
  alarmsManager: Manager;
  enhancedQueryParams: Record<string, unknown>;
  forceDataRefresh: number;
  handleSensorLoadingStateChange: (isLoading: boolean) => void;
  handleOilLoadingStateChange: (isLoading: boolean) => void;
  handleCoolantLoadingStateChange: (isLoading: boolean) => void;
  loadingStates: Record<string, boolean>;
  alarmGraphsData: Record<string, unknown>;
  selectedComponentId?: string | null;
  selectedComponentObj?: ComponentData;
  setTriggerSensorReset: (value: boolean) => void;
  setTriggerOilReset: (value: boolean) => void;
  setTriggerCoolantReset: (value: boolean) => void;
  setTriggerAlarmReset: (value: boolean) => void;
  verticalLinesState?: unknown;
  rawVerticalLinesState?: {
    verticalLines?: DataLayerVerticalLine[];
    removeVerticalLine?: (id: string) => void;
    [key: string]: unknown;
  };
  spectrumVerticalLinesState?: ReturnType<typeof useVerticalLines>;
  benchmarkLineBySection?: Record<string, string | null>;
  setBenchmarkLineBySection?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  config?: { dataLayers?: Array<{ id: string; filters?: BackendFilter[] }> };
  legendItemStates?: Record<string, boolean>;
  toggleLegendItem?: (itemId: string, enabled: boolean) => void;
  onToggleSensor?: (sectionId: string, sensorName: string) => void;
  onAddSensor?: (sectionId: string, sensorName: string) => void;
  registerSensorToggleHandler?: (dataLayer: string, handler: (sectionId: string, sensorName: string) => void) => void;
  registerSensorData?: (
    dataLayer: string,
    enabledSensors: Record<string, boolean>,
    sensorColors: Record<string, string>
  ) => void;
  allEnabledSensors?: Record<string, Record<string, boolean>>;
  allSensorColors?: Record<string, Record<string, string>>;
  masterSensorColorMap?: Record<string, string>; // Global sensor name -> color mapping for consistency across all data layers
  isSensorLoading?: (dataLayer: string, sectionId: string, sensorName: string) => boolean;
  // Interval lines props
  intervalLines?: IntervalLine[];
  onOpenIntervalDialog?: (key: string) => void;
  onClearIntervalLines?: () => void;
  removeIntervalLine?: (id: string) => void;
  // Display mode
  displayMode?: 'sensor-fusion' | 'spectrum' | 'waveform';
  // Props for NewSpectrum
  asset?: AssetData;
  components?: ComponentData[];
  spectrumConfig?: Record<string, unknown>;
  activeOperationalModes?: string[];
  // Refs for data layer configuration (React Rules of Hooks compliance)
  groupFilterResetRef?: React.MutableRefObject<(() => void) | null>;
  initialAlarmLegendStatesRef?: React.MutableRefObject<Record<string, boolean> | null>;
  // Global loading sensors state - synchronized across all sections
  loadingSensors?: Set<string>;
  setLoadingSensor?: (sensorName: string) => void;
  clearLoadingSensor?: (sensorName: string) => void;
  // Synced tool selection states (for syncTools feature)
  markerSelectionModeByChart?: Record<string, boolean>;
  setMarkerSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  harmonicSelectionModeByChart?: Record<string, boolean>;
  setHarmonicSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sideBandsSelectionModeByChart?: Record<string, boolean>;
  setSideBandsSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  customLineModeByChart?: Record<string, string | null>;
  setCustomLineModeByChart?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  // Focus mode props
  legendSync?: {
    mode: 'multi' | 'focus';
    toggleMode: () => void;
    getSavedState?: () => Record<string, Record<string, boolean>> | null;
    clearSavedState?: () => void;
  };
  focusedSensors?: Record<string, string>; // Map of sectionId -> focused sensor name
  onFocus?: (sensorName: string, sectionId?: string) => void;
  // Selected marker timestamp from Spectrum for highlighting in SensorFusion
  selectedMarkerTimestamp?: string | null;
  setSelectedMarkerTimestamp?: (timestamp: string | null) => void;
  // Selected timestamps set for focus mode multi-select
  selectedTimestampsSet?: Set<string>;
  setSelectedTimestampsSet?: (timestamps: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  // Expandable legend series for sensors (vibrations)
  sensorsExpandableLegendSeries?: Array<Record<string, unknown>>;
  onToggleExpandableSeries?: (seriesId: string) => void;
  onToggleExpandableSeriesFocus?: (seriesId: string) => void;
  onToggleExpandableTimestamp?: (seriesId: string, timestampId: string) => void;
}

export const createDataLayersConfig = (props: DataLayersConfigProps): DataLayerConfig[] => {
  const {
    activeDataLayers,
    isMobileAsset,
    assetName,
    diagnostics,
    baseTitle,
    from,
    to,
    sensorGraphs,
    oilGraphs,
    coolantGraphs,
    alarmGraphs,
    sensorsManager,
    oilManager,
    coolantManager,
    alarmsManager,
    enhancedQueryParams,
    forceDataRefresh,
    handleSensorLoadingStateChange,
    handleOilLoadingStateChange,
    handleCoolantLoadingStateChange,
    loadingStates,
    alarmGraphsData,
    selectedComponentId,
    selectedComponentObj,
    setTriggerSensorReset,
    setTriggerOilReset,
    setTriggerCoolantReset,
    setTriggerAlarmReset,
    verticalLinesState,
    rawVerticalLinesState,
    spectrumVerticalLinesState,
    benchmarkLineBySection,
    setBenchmarkLineBySection,
    config,
    legendItemStates,
    toggleLegendItem,
    onToggleSensor,
    onAddSensor,
    registerSensorToggleHandler,
    registerSensorData,
    allEnabledSensors,
    allSensorColors,
    masterSensorColorMap,
    isSensorLoading,
    intervalLines,
    onOpenIntervalDialog,
    onClearIntervalLines,
    removeIntervalLine,
    displayMode = 'sensor-fusion',
    asset,
    components,
    spectrumConfig,
    activeOperationalModes = [],
    groupFilterResetRef,
    initialAlarmLegendStatesRef,
    loadingSensors,
    setLoadingSensor,
    clearLoadingSensor,
    markerSelectionModeByChart,
    setMarkerSelectionModeByChart,
    harmonicSelectionModeByChart,
    setHarmonicSelectionModeByChart,
    sideBandsSelectionModeByChart,
    setSideBandsSelectionModeByChart,
    customLineModeByChart,
    setCustomLineModeByChart,
    legendSync,
    focusedSensors,
    onFocus,
    selectedMarkerTimestamp,
    setSelectedMarkerTimestamp,
    selectedTimestampsSet,
    setSelectedTimestampsSet,
    sensorsExpandableLegendSeries,
    onToggleExpandableSeries,
    onToggleExpandableSeriesFocus,
    onToggleExpandableTimestamp,
  } = props;

  return [
    {
      id: SENSOR_FUSION_DATA_LAYERS.AI_TREND,
      condition: () => activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.AI_TREND),
      component: () => (
        <FusionTrendCard
          assetName={assetName || ''}
          diagnostics={diagnostics}
          isLoading={false}
          showMaintenance={false}
          baseTitle={baseTitle || ''}
        />
      ),
    },
    {
      id: SENSOR_FUSION_DATA_LAYERS.SENSORS,
      condition: () => activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.SENSORS),
      component: () => {
        // Get filters from config for sensors data layer
        const sensorsDataLayer = config?.dataLayers?.find((layer: any) => layer.id === 'sensors');
        let sensorsFilters = transformFilters(sensorsDataLayer?.filters || []);

        // Check if there's a search filter from backend
        const backendSearchFilter = sensorsFilters.find((f) => f.type === 'search');

        // GENERIC: Control section selection based on display mode configuration
        // CRITICAL: Always pass ALL graphs to container to keep sensor map stable
        // Only control which sections are SELECTED/VISIBLE, not which graphs are passed
        const currentModeConfig = SENSOR_FUSION_DISPLAY_MODES[displayMode];

        // Check for visible/hidden graph filters (for SELECTION only, not for filtering graphs array)
        const visibleGraphFilter = currentModeConfig?.visibleGraphsInLayers?.[SENSOR_FUSION_DATA_LAYERS.SENSORS];
        const hiddenGraphFilter = currentModeConfig?.hiddenGraphsInLayers?.[SENSOR_FUSION_DATA_LAYERS.SENSORS];

        // Determine which graphs should be SELECTED (not filtered out)
        let selectedGraphIds: string[] = sensorsManager.selectedSections;

        if (visibleGraphFilter) {
          // Whitelist approach: select only graphs that match the filter
          selectedGraphIds = sensorGraphs
            .filter((graph: any) => visibleGraphFilter(graph))
            .map((graph: any) => graph.id);
        } else if (hiddenGraphFilter) {
          // Blacklist approach: select graphs that don't match the filter
          selectedGraphIds = sensorGraphs
            .filter((graph: any) => !hiddenGraphFilter(graph))
            .map((graph: any) => graph.id);
        }

        // GENERIC: Auto-select filtered graphs if configured in display mode
        const shouldAutoSelect =
          currentModeConfig?.autoSelectFilteredGraphs?.[SENSOR_FUSION_DATA_LAYERS.SENSORS] ?? false;
        const selectedSectionsOverride = shouldAutoSelect ? selectedGraphIds : sensorsManager.selectedSections;

        // DEBUG: Log selection results

        // GENERIC: Check if UI elements should be visible/hidden
        const visibleUIElements = currentModeConfig?.visibleUIElements?.[SENSOR_FUSION_DATA_LAYERS.SENSORS];
        const hiddenUIElements = currentModeConfig?.hiddenUIElements?.[SENSOR_FUSION_DATA_LAYERS.SENSORS];

        let shouldHideManagedGraphsDropdown = false;
        if (visibleUIElements) {
          // Whitelist approach: hide if not in visible list
          shouldHideManagedGraphsDropdown = !visibleUIElements.includes('managedGraphsDropdown');
        } else if (hiddenUIElements) {
          // Blacklist approach: hide if in hidden list
          shouldHideManagedGraphsDropdown = hiddenUIElements.includes('managedGraphsDropdown');
        }

        // Apply legend item states to ALL graphs (not filtered)
        // Container needs ALL graphs to maintain stable sensor map
        const graphsWithLegendState = sensorGraphs.map((graph: any) => ({
          ...graph,
          legend: graph.legend?.map((item: any) => ({
            ...item,
            enabled: legendItemStates?.[item.id] !== undefined ? legendItemStates[item.id] : item.enabled !== false,
          })),
        }));

        // Create search options from actual sensor data (like comparison table)

        const sensorsDataLayerData = allEnabledSensors?.[SENSOR_FUSION_DATA_LAYERS.SENSORS] || {};
        const sensorsColorData = allSensorColors?.[SENSOR_FUSION_DATA_LAYERS.SENSORS] || {};

        // Create search options with section information
        const searchOptions: any[] = [];

        // Go through each graph to find which section each sensor belongs to
        sensorGraphs.forEach((graph: any) => {
          const sectionTitle = graph.title || graph.id;

          // Check for sensors in this graph's legend or sensorUiNames
          const sensorNames =
            graph.sensorUiNames || graph.legend?.map((item: any) => item.displayName || item.name) || [];

          sensorNames.forEach((sensorName: string) => {
            if (Object.prototype.hasOwnProperty.call(sensorsDataLayerData, sensorName)) {
              searchOptions.push({
                value: sensorName,
                label: sensorName,
                unit: graph.unit || '',
                enabled: sensorsDataLayerData[sensorName],
                color: sensorsColorData[sensorName],
                section: sectionTitle, // Add section information for display/scrolling
                sectionId: graph.id, // Add section ID for toggle handlers
              });
            }
          });
        });

        // If no sensor data, show a warning
        if (searchOptions.length === 0) {
        }

        // If backend provides a search filter with useLocalLegend, populate it with sensor data
        if (backendSearchFilter && backendSearchFilter.searchConfig?.useLocalLegend) {
          // Update the backend search filter with actual sensor options (DIRECT MUTATION like main)
          backendSearchFilter.options = searchOptions;
        } else if (!backendSearchFilter) {
          // If no search filter from backend, create one
          const legendSearchFilter: DataLayerFilter = {
            id: 'legend_search',
            label: 'Search',
            type: 'search' as const,
            isMultiSelect: true,
            options: searchOptions,
            handler: (sensorName: any, filter: any) => {
              // Search handler
            },
          };
          sensorsFilters = [...sensorsFilters, legendSearchFilter];
        } else {
        }

        return (
          <ManagedGraphsSection
            title={`${baseTitle} Sensors`}
            dataLayer={SENSOR_FUSION_DATA_LAYERS.SENSORS}
            graphs={graphsWithLegendState}
            selectedSections={selectedSectionsOverride}
            onSelectionChange={sensorsManager.handleSelectionChange}
            showReset={true}
            triggerReset={sensorsManager.triggerReset}
            onReset={() => {
              if (toggleLegendItem) {
                const initialStates: Record<string, boolean> = {};
                sensorGraphs.forEach((graph: any) => {
                  graph.legend?.forEach((item: any) => {
                    const initialEnabled = item.enabled !== false;
                    initialStates[item.id] = initialEnabled;
                    if (legendItemStates?.[item.id] !== initialEnabled) {
                      toggleLegendItem(item.id, initialEnabled);
                    }
                  });
                });
              }

              setTriggerSensorReset(true);
              sensorsManager.handleReset();
            }}
            onResetDataLayer={() => {
              sensorsManager.handleDataLayerReset();
              setTriggerSensorReset(false);
            }}
            loadingState={sensorsManager.loadingState}
            queryParams={(() => {
              const params = { ...enhancedQueryParams, data_layer: SENSOR_FUSION_DATA_LAYERS.SENSORS };
              return params;
            })()}
            forceRefresh={forceDataRefresh}
            onLoadingStateChange={(isLoading: boolean) => {
              sensorsManager.handleLoadingStateChange(isLoading);
              handleSensorLoadingStateChange(isLoading);
            }}
            includeAlarms={false}
            variant="lines"
            verticalLinesState={verticalLinesState}
            benchmarkLineBySection={benchmarkLineBySection}
            setBenchmarkLineBySection={setBenchmarkLineBySection}
            filters={sensorsFilters}
            loadingSensors={loadingSensors}
            setLoadingSensor={setLoadingSensor}
            clearLoadingSensor={clearLoadingSensor}
            onToggleSensor={onToggleSensor}
            onAddSensor={onAddSensor}
            registerSensorToggleHandler={registerSensorToggleHandler}
            registerSensorData={registerSensorData}
            sectionEnabledSensors={{ sensors: allEnabledSensors?.[SENSOR_FUSION_DATA_LAYERS.SENSORS] || {} }}
            sectionSensorColors={{ sensors: allSensorColors?.[SENSOR_FUSION_DATA_LAYERS.SENSORS] || {} }}
            masterSensorColorMap={masterSensorColorMap}
            isSensorLoading={(sectionId: string, sensorName: string) =>
              isSensorLoading ? isSensorLoading(SENSOR_FUSION_DATA_LAYERS.SENSORS, sectionId, sensorName) : false
            }
            onFilterChange={(filterId: string, value: any) => {
              // General filter change handler for sensors
              // The search filter handles sensor toggling through onToggleSensor/onAddSensor
            }}
            hideManagedGraphsDropdown={shouldHideManagedGraphsDropdown}
            enableCompareTable={false}
            enableMarkers={true}
            markerLabel="Timestamps"
            syncTools={{ markers: true }}
            markerSelectionModeByChart={markerSelectionModeByChart}
            setMarkerSelectionModeByChart={setMarkerSelectionModeByChart}
            harmonicSelectionModeByChart={harmonicSelectionModeByChart}
            setHarmonicSelectionModeByChart={setHarmonicSelectionModeByChart}
            sideBandsSelectionModeByChart={sideBandsSelectionModeByChart}
            setSideBandsSelectionModeByChart={setSideBandsSelectionModeByChart}
            customLineModeByChart={customLineModeByChart}
            setCustomLineModeByChart={setCustomLineModeByChart}
            // Focus mode props (legends are NOT synced, but focus still works)
            syncSectionLegends={false}
            legendSyncMode={legendSync?.mode}
            showLegendModeToggle={true}
            focusedSensors={focusedSensors}
            onFocus={onFocus}
            onToggleFocusMode={legendSync?.toggleMode}
            selectedMarkerTimestamp={selectedMarkerTimestamp}
            selectedTimestampsSet={selectedTimestampsSet}
            showFloatingToolbar={false}
          />
        );
      },
    },
    {
      id: SENSOR_FUSION_DATA_LAYERS.SPECTRUM,
      condition: () => displayMode === 'spectrum', // Only render when Spectrum tab is active
      component: () => (
        <NewSpectrum
          asset={asset}
          components={components || []}
          diagnostics={diagnostics}
          config={spectrumConfig}
          activeOperationalModes={activeOperationalModes}
          activeDataLayers={[SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS]}
          triggerRefresh={forceDataRefresh}
          sensorFusionVerticalLines={rawVerticalLinesState}
          onRemoveSensorFusionMarker={(markerId) => {
            if (rawVerticalLinesState?.removeVerticalLine) {
              rawVerticalLinesState.removeVerticalLine(markerId);
            }
          }}
          onClearAllSensorFusionMarkers={() => {
            // Clear all user markers (non-harmonic markers)
            if (rawVerticalLinesState?.verticalLines) {
              const userMarkers = rawVerticalLinesState.verticalLines.filter(
                (line: any) => line.markerType === 'user' && !line.isHarmonic
              );
              userMarkers.forEach((marker: any) => {
                if (rawVerticalLinesState?.removeVerticalLine) {
                  rawVerticalLinesState.removeVerticalLine(marker.id);
                }
              });
            }
          }}
          selectedMarkerTimestamp={selectedMarkerTimestamp}
          setSelectedMarkerTimestamp={setSelectedMarkerTimestamp}
          selectedTimestampsSet={selectedTimestampsSet}
          setSelectedTimestampsSet={setSelectedTimestampsSet}
          masterSensorColorMap={masterSensorColorMap}
          spectrumVerticalLinesState={spectrumVerticalLinesState}
          allowMultiTimestampMode={true} // Temporarily disabled - focus mode uses single-select like sensors (opacity-based)
        />
      ),
    },
    {
      id: SENSOR_FUSION_DATA_LAYERS.MAINTENANCE,
      condition: () => activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.MAINTENANCE),
      component: () => <MaintenanceCard dateRange={{ from, to }} />,
    },
    {
      id: SENSOR_FUSION_DATA_LAYERS.ALARMS,
      condition: () => isMobileAsset && activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.ALARMS),
      component: () => {
        // Get filters from config data layer
        const alarmDataLayer = config?.dataLayers?.find((layer: any) => layer.id === 'alarm');
        let alarmFilters = transformFilters(alarmDataLayer?.filters || []);

        // Add handler to group filter for filtering legend items
        const groupFilterIndex = alarmFilters.findIndex((f) => f.type === 'group');
        if (groupFilterIndex >= 0 && toggleLegendItem) {
          const originalGroupFilter = alarmFilters[groupFilterIndex];
          alarmFilters[groupFilterIndex] = {
            ...originalGroupFilter,
            resetHandler: (resetCallback: () => void) => {
              // Store the reset callback so we can call it on reset
              if (groupFilterResetRef) {
                groupFilterResetRef.current = resetCallback;
              }
            },
            handler: (value: string | string[] | Record<string, string[]>, filterConfig: DataLayerFilter) => {
              /**
               * Group Filter Handler - Applies severity/priority/interface filtering
               *
               * IMPORTANT BEHAVIOR:
               * - Filters are ONLY applied to graphs that are currently enabled in "Manage Graphs"
               * - Disabled/non-selected graphs are completely ignored by the filter
               * - This preserves the sensor states in hidden graphs, preventing unintended changes
               *
               * Example:
               * - User has Graph A (enabled) and Graph B (disabled)
               * - Graph A sensors: Sensor1 (on), Sensor2 (off)
               * - Graph B sensors: Sensor3 (on), Sensor4 (on)
               * - When filter is applied, only Graph A sensors can be affected
               * - Graph B sensors remain untouched regardless of filter settings
               */

              const selections =
                typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, string[]>) : {};

              // Extract selections from the group filter
              // The backend should provide filters with these specific IDs for alarms
              const severities = selections.severity || [];
              const priorities = selections.priority || [];
              const interfaces = selections.interface || [];

              // Filter Logic:
              // - If ALL filter groups have no selections, nothing matches (empty state)
              // - If a filter group has selections, items must match one of them
              // - Items are only shown if they match ALL active filter criteria

              // Process each graph and legend item
              const updatePromises: Array<() => void> = [];

              alarmGraphs.forEach((graph: any) => {
                // CRITICAL: Check if this graph is currently enabled/visible
                // Only graphs selected in "Manage Graphs" dropdown should be affected by filters
                const isGraphEnabled = alarmsManager.selectedSections.includes(graph.id);
                if (!isGraphEnabled) {
                  // This graph is hidden/disabled - DO NOT modify any of its sensors
                  // Preserves the current state of all sensors in non-managed graphs
                  return;
                }

                graph.legend?.forEach((item: any) => {
                  // Get item metadata - check various possible fields
                  const itemSeverity = item.severity || item.metadata?.severity || '';
                  const itemPriority = item.priority || item.metadata?.priority || '';
                  const itemInterface = item.interface || item.metadata?.interface || '';

                  // Check if item matches the selected filters
                  // NOTE: This filtering ONLY happens for enabled graphs (checked above)
                  // Sensors in disabled graphs are never processed here

                  // Filter matching logic:
                  // - If ANY filter group has no selections, show no data (empty state)
                  // - If a filter group has selections, the item must match one of them
                  // - The item is only enabled if it matches ALL active filter categories

                  // Check if ANY filter group is empty
                  // Only check filters that exist in the selections (backend may not provide all three)
                  const activeFilters = [];
                  if ('severity' in selections) activeFilters.push(severities);
                  if ('priority' in selections) activeFilters.push(priorities);
                  if ('interface' in selections) activeFilters.push(interfaces);

                  const anyFilterEmpty = activeFilters.some((filter) => filter.length === 0);

                  // If any filter is empty, nothing should be shown
                  if (anyFilterEmpty) {
                    const shouldBeEnabled = false;
                    const currentState = legendItemStates?.[item.id] ?? false;

                    if (currentState !== shouldBeEnabled && toggleLegendItem) {
                      updatePromises.push(() => {
                        toggleLegendItem(item.id, shouldBeEnabled);
                      });
                    }
                    return; // Skip to next item
                  }

                  // All filters have selections, check if item matches
                  // Only check filters that are actually present in selections
                  let shouldBeEnabled = true;

                  if ('severity' in selections && severities.length > 0) {
                    shouldBeEnabled = shouldBeEnabled && severities.includes(itemSeverity);
                  }

                  if ('priority' in selections && priorities.length > 0) {
                    shouldBeEnabled = shouldBeEnabled && priorities.includes(itemPriority);
                  }

                  if ('interface' in selections && interfaces.length > 0) {
                    shouldBeEnabled = shouldBeEnabled && interfaces.includes(itemInterface);
                  }
                  const currentState = legendItemStates?.[item.id] ?? false; // Default to disabled

                  if (currentState !== shouldBeEnabled && toggleLegendItem) {
                    updatePromises.push(() => {
                      toggleLegendItem(item.id, shouldBeEnabled);
                    });
                  }
                });
              });

              updatePromises.forEach((update) => update());
            },
          };
        }

        // Capture initial legend states on first render (for reset functionality)
        if (initialAlarmLegendStatesRef && initialAlarmLegendStatesRef.current === null) {
          const initialStates: Record<string, boolean> = {};
          alarmGraphs.forEach((graph: any) => {
            graph.legend?.forEach((item: any) => {
              // Store the initial enabled state (from backend or default)
              initialStates[item.id] = item.enabled !== false;
            });
          });
          initialAlarmLegendStatesRef.current = initialStates;
        }

        // Apply legend item states to alarm graphs
        const alarmGraphsWithLegendState = alarmGraphs.map((graph: any) => ({
          ...graph,
          legend: graph.legend?.map((item: any) => {
            const enabled = legendItemStates?.[item.id] !== undefined ? legendItemStates[item.id] : item.enabled !== false;
            return {
              ...item,
              enabled,
              // Preserve original metadata
              priority: item.priority,
              severity: item.severity,
              interface: item.interface,
            };
          }),
        }));

        // Create search options from actual alarm sensor data (like sensors section)
        const alarmDataLayerData = allEnabledSensors?.[SENSOR_FUSION_DATA_LAYERS.ALARMS] || {};
        const alarmColorData = allSensorColors?.[SENSOR_FUSION_DATA_LAYERS.ALARMS] || {};

        // Create alarm search options with section information
        const alarmLegendItems: any[] = [];

        // Go through each alarm graph to find which section each alarm belongs to
        alarmGraphs.forEach((graph: any) => {
          const sectionTitle = graph.title || graph.id;

          // Check for alarms in this graph's legend
          const alarmNames = graph.legend?.map((item: any) => item.displayName || item.name) || [];

          alarmNames.forEach((alarmName: string) => {
            if (Object.prototype.hasOwnProperty.call(alarmDataLayerData, alarmName)) {
              alarmLegendItems.push({
                value: alarmName,
                label: alarmName,
                unit: '', // Remove 'Alarm' since section shows this info
                enabled: alarmDataLayerData[alarmName],
                color: alarmColorData[alarmName],
                section: sectionTitle, // This replaces the 'Alarm' text
                sectionId: graph.id, // Add section ID for toggle handlers
              });
            }
          });
        });

        const alarmLegendSearchFilter: DataLayerFilter = {
          id: 'legend_search',
          label: 'Search',
          type: 'search' as const,
          isMultiSelect: true,
          options: alarmLegendItems,
          handler: (sensorName: any) => {
            // Handled by onToggleSensor/onAddSensor props
          },
        };

        // Replace mock search filter with actual legend items
        const searchFilterIndex = alarmFilters.findIndex((f) => f.type === 'search');
        if (searchFilterIndex >= 0) {
          alarmFilters[searchFilterIndex] = alarmLegendSearchFilter;
        } else if (!alarmFilters.find((f) => f.type === 'search')) {
          alarmFilters = [...alarmFilters, alarmLegendSearchFilter];
        }

        return (
          <ManagedGraphsSection
            title={
              selectedComponentId !== 'all-components'
                ? `${selectedComponentObj?.component_ui_name ?? selectedComponentObj?.component_name} Alarms`
                : 'Total Asset Alarms By Component'
            }
            dataLayer={SENSOR_FUSION_DATA_LAYERS.ALARMS}
            graphs={alarmGraphsWithLegendState}
            selectedSections={alarmsManager.selectedSections}
            onSelectionChange={alarmsManager.handleSelectionChange}
            showReset={true}
            triggerReset={alarmsManager.triggerReset}
            onReset={() => {
              setTriggerAlarmReset(true);
              alarmsManager.handleReset();

              // Reset legend items to initial state
              // This will:
              // 1. Remove any sensors that were added via search
              // 2. Restore sensors that were disabled to their initial state
              // 3. Ensure only initially enabled sensors remain active
              if (initialAlarmLegendStatesRef && initialAlarmLegendStatesRef.current && toggleLegendItem) {
                Object.entries(initialAlarmLegendStatesRef.current).forEach(([itemId, shouldBeEnabled]) => {
                  const currentState = legendItemStates?.[itemId];
                  if (currentState !== shouldBeEnabled) {
                    toggleLegendItem(itemId, shouldBeEnabled);
                  }
                });
              }

              // Reset group filter to initial state
              // This calls the reset callback that was registered by GroupDataLayerFilters
              // which will restore the initial filter selections and re-apply them
              if (groupFilterResetRef && groupFilterResetRef.current) {
                groupFilterResetRef.current();
              }
            }}
            onResetDataLayer={() => {
              alarmsManager.handleDataLayerReset();
              setTriggerAlarmReset(false);
            }}
            loadingState={false}
            queryParams={{ ...enhancedQueryParams, data_layer: SENSOR_FUSION_DATA_LAYERS.ALARMS }}
            forceRefresh={forceDataRefresh}
            includeAlarms={true}
            variant="columns"
            verticalLinesState={verticalLinesState}
            filters={alarmFilters}
            onFilterChange={(filterId: string, value: any) => {
              // General filter change handler
              // The search filter handles sensor toggling through onToggleSensor/onAddSensor
              // Other filters could trigger data refetch or state updates here
            }}
            externalLegendStates={legendItemStates}
            onLegendToggle={toggleLegendItem}
            onToggleSensor={onToggleSensor}
            onAddSensor={onAddSensor}
            registerSensorToggleHandler={registerSensorToggleHandler}
            registerSensorData={registerSensorData}
            sectionEnabledSensors={{ alarms: allEnabledSensors?.[SENSOR_FUSION_DATA_LAYERS.ALARMS] || {} }}
            sectionSensorColors={{ alarms: allSensorColors?.[SENSOR_FUSION_DATA_LAYERS.ALARMS] || {} }}
            enableMarkers={true}
            markerLabel="Timestamps"
            // Focus mode props - each alarm section has its own independent legend (like sensors)
            syncSectionLegends={false}
            legendSyncMode={legendSync?.mode}
            showLegendModeToggle={true}
            focusedSensors={focusedSensors}
            onFocus={onFocus}
            onToggleFocusMode={legendSync?.toggleMode}
            showFloatingToolbar={false}
          />
        );
      },
    },
    {
      id: SENSOR_FUSION_DATA_LAYERS.OIL_REPORT,
      condition: () => activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.OIL_REPORT),
      component: () => {
        // Get filters from config for oil data layer
        const oilDataLayer = config?.dataLayers?.find((layer: any) => layer.id === 'oil_report');
        const oilFilters = transformFilters(oilDataLayer?.filters || []);

        return (
          <ManagedGraphsSection
            title="Oil Reports for Last 6 Months"
            dataLayer={SENSOR_FUSION_DATA_LAYERS.OIL_REPORT}
            graphs={oilGraphs}
            selectedSections={oilManager.selectedSections}
            onSelectionChange={oilManager.handleSelectionChange}
            showReset={true}
            triggerReset={oilManager.triggerReset}
            onReset={() => {
              setTriggerOilReset(true);
              oilManager.handleReset();
            }}
            onResetDataLayer={() => {
              oilManager.handleDataLayerReset();
              setTriggerOilReset(false);
            }}
            loadingState={oilManager.loadingState}
            queryParams={{ ...enhancedQueryParams, data_layer: SENSOR_FUSION_DATA_LAYERS.OIL_REPORT }}
            forceRefresh={forceDataRefresh}
            onLoadingStateChange={(isLoading: boolean) => {
              oilManager.handleLoadingStateChange(isLoading);
              handleOilLoadingStateChange(isLoading);
            }}
            includeAlarms={false}
            variant="lines"
            verticalLinesState={verticalLinesState}
            filters={oilFilters}
            onFilterChange={(filterId: string, value: any) => {}}
            enableMarkers={true}
            markerLabel="Timestamps"
            // Focus mode props
            syncSectionLegends={true}
            legendSyncMode={legendSync?.mode}
            showLegendModeToggle={true}
            focusedSensors={focusedSensors}
            onFocus={onFocus}
            onToggleFocusMode={legendSync?.toggleMode}
            showFloatingToolbar={false}
          />
        );
      },
    },
    {
      id: SENSOR_FUSION_DATA_LAYERS.COOLANT,
      condition: () => activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.COOLANT),
      component: () => {
        // Get filters from config for coolant data layer
        const coolantDataLayer = config?.dataLayers?.find((layer: any) => layer.id === 'coolant');
        const coolantFilters = transformFilters(coolantDataLayer?.filters || []);

        return (
          <ManagedGraphsSection
            title="Coolant Reports for Last 6 Months"
            dataLayer={SENSOR_FUSION_DATA_LAYERS.COOLANT}
            graphs={coolantGraphs}
            selectedSections={coolantManager.selectedSections}
            onSelectionChange={coolantManager.handleSelectionChange}
            showReset={true}
            triggerReset={coolantManager.triggerReset}
            onReset={() => {
              setTriggerCoolantReset(true);
              coolantManager.handleReset();
            }}
            onResetDataLayer={() => {
              coolantManager.handleDataLayerReset();
              setTriggerCoolantReset(false);
            }}
            loadingState={coolantManager.loadingState}
            queryParams={{ ...enhancedQueryParams, data_layer: SENSOR_FUSION_DATA_LAYERS.COOLANT }}
            forceRefresh={forceDataRefresh}
            onLoadingStateChange={(isLoading: boolean) => {
              coolantManager.handleLoadingStateChange(isLoading);
              handleCoolantLoadingStateChange(isLoading);
            }}
            includeAlarms={false}
            variant="lines"
            verticalLinesState={verticalLinesState}
            filters={coolantFilters}
            onFilterChange={(filterId: string, value: any) => {}}
            enableMarkers={true}
            markerLabel="Timestamps"
            // Focus mode props
            syncSectionLegends={true}
            legendSyncMode={legendSync?.mode}
            showLegendModeToggle={true}
            focusedSensors={focusedSensors}
            onFocus={onFocus}
            onToggleFocusMode={legendSync?.toggleMode}
            showFloatingToolbar={false}
          />
        );
      },
    },
  ];
};

/**
 * Render order for data layers
 */
export const DATA_LAYERS_RENDER_ORDER = [
  SENSOR_FUSION_DATA_LAYERS.AI_TREND,
  SENSOR_FUSION_DATA_LAYERS.SENSORS,
  SENSOR_FUSION_DATA_LAYERS.SPECTRUM,
  SENSOR_FUSION_DATA_LAYERS.OIL_REPORT,
  SENSOR_FUSION_DATA_LAYERS.COOLANT,
  SENSOR_FUSION_DATA_LAYERS.ALARMS,
  SENSOR_FUSION_DATA_LAYERS.MAINTENANCE,
];
